import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Lantern from './Lantern';
import FloatingParticles from './FloatingParticles';

interface SiamIntroProps {
  onComplete: () => void;
  duration?: number;
  welcomeName?: string;
}

export default function SiamIntro({ onComplete, duration = 4, welcomeName }: SiamIntroProps) {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setVisible(false);
    }, duration * 1000);
    return () => clearTimeout(timer);
  }, [duration]);

  return (
    <AnimatePresence onExitComplete={onComplete}>
      {visible && (
        <motion.div
          initial={{ opacity: 1 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.2, ease: 'easeInOut' }}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            overflow: 'hidden',
            background: '#000',
          }}
        >
          {/* Tropical Bangkok Background */}
          <div style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to bottom, #000, #140b08, #2c1209)',
          }} />

          {/* Golden ambient glow */}
          <div style={{
            position: 'absolute',
            inset: 0,
            background: 'radial-gradient(circle at top, rgba(255,180,60,0.15), transparent 45%)',
          }} />

          {/* Rooftop haze */}
          <div style={{
            position: 'absolute',
            bottom: 0,
            width: '100%',
            height: '40%',
            background: 'linear-gradient(to top, rgba(230, 126, 34, 0.1), transparent)',
          }} />

          {/* Particles */}
          <FloatingParticles count={25} />

          {/* Lanterns */}
          <Lantern left="10%" delay={0} size={0.9} />
          <Lantern left="25%" delay={1} size={1.2} />
          <Lantern left="50%" delay={2} size={1} />
          <Lantern left="70%" delay={3} size={1.1} />
          <Lantern left="85%" delay={1.5} size={0.8} />

          {/* Main Content */}
          <div style={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            textAlign: 'center',
            padding: '0 24px',
          }}>
            {/* Logo glow */}
            <motion.div
              initial={{ scale: 0.7, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 1.8 }}
              style={{ position: 'relative' }}
            >
              <div style={{
                position: 'absolute',
                inset: 0,
                filter: 'blur(30px)',
                background: 'rgba(255, 167, 38, 0.3)',
                borderRadius: '50%',
                transform: 'scale(2)',
              }} />
              <h1 style={{
                position: 'relative',
                fontSize: 'clamp(48px, 10vw, 96px)',
                fontWeight: 900,
                letterSpacing: '0.3em',
                textTransform: 'uppercase',
                background: 'linear-gradient(to right, #FFF5E0, #C9A96E, #E67E22)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                margin: 0,
              }}>
                LE SIAM
              </h1>
            </motion.div>

            {/* Welcome name (post-login variant) */}
            {welcomeName && (
              <motion.p
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 1 }}
                style={{
                  marginTop: 20,
                  color: '#FFD54F',
                  fontSize: 'clamp(18px, 4vw, 28px)',
                  fontWeight: 600,
                  letterSpacing: '0.1em',
                  textShadow: '0 0 20px rgba(255,213,79,0.5)',
                }}
              >
                Bienvenue, {welcomeName}
              </motion.p>
            )}

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 1.2 }}
              style={{
                marginTop: welcomeName ? 12 : 24,
                color: 'rgba(255, 224, 178, 0.8)',
                letterSpacing: '0.25em',
                textTransform: 'uppercase',
                fontSize: 'clamp(12px, 2vw, 16px)',
              }}
            >
              Thai Rooftop Experience • Saint Martin
            </motion.p>

            {/* Loader bar */}
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: 220 }}
              transition={{ delay: 1.5, duration: 2 }}
              style={{
                position: 'relative',
                height: 2,
                marginTop: 48,
                background: 'rgba(255, 255, 255, 0.1)',
                borderRadius: 1,
                overflow: 'hidden',
              }}
            >
              <motion.div
                animate={{ x: ['-100%', '250%'] }}
                transition={{ duration: 1.4, repeat: Infinity, ease: 'linear' }}
                style={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  width: 96,
                  background: 'linear-gradient(to right, transparent, #FFD54F, transparent)',
                  filter: 'blur(2px)',
                }}
              />
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}